import React, { useState } from "react";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "./ui/input-otp";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

// Spinner icon for loading states
const RefreshIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M8 16H3v5" />
  </svg>
);

/**
 * PromoCodeBlock — Two-step access code flow:
 *   Step A (Graduation Gate): Collects graduation month/year if not already set.
 *   Step B (Code Input): 6-character OTP input for promo or org codes.
 *
 * Props:
 *   - userEmail: current user's email
 *   - userId: current user's Firebase UID
 *   - graduationMonth: existing graduation month from user doc (skip gate if set)
 *   - graduationYear: existing graduation year from user doc (skip gate if set)
 */
const PromoCodeBlock = ({ userEmail, userId, graduationMonth, graduationYear }) => {
  // Determine if we should skip the graduation gate (already filled in)
  const hasGraduationInfo = !!(graduationMonth && graduationYear);

  // Step A state — graduation gate
  const [step, setStep] = useState(hasGraduationInfo ? "code" : "graduation"); // "graduation" or "code"
  const [selectedMonth, setSelectedMonth] = useState(graduationMonth || "");
  const [selectedYear, setSelectedYear] = useState(graduationYear || "");
  const [gradSaving, setGradSaving] = useState(false);
  const [gradError, setGradError] = useState("");

  // Step B state — code input
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // --- Step A: Graduation Gate handlers ---

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 2060 - currentYear + 1 }, (_, i) => currentYear + i);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  // Save graduation month/year to both users and whitelist docs, then advance to code step
  const handleGraduationSubmit = async () => {
    if (!selectedMonth || !selectedYear) {
      setGradError("Please select both your graduation month and year.");
      return;
    }

    setGradSaving(true);
    setGradError("");

    try {
      const gradData = {
        graduationMonth: Number(selectedMonth),
        graduationYear: Number(selectedYear),
      };

      // Update users collection
      if (userId) {
        const userRef = doc(db, "users", userId);
        await updateDoc(userRef, gradData);
      }

      // Update whitelist collection (query by email)
      if (userEmail) {
        const { collection, query, where, getDocs } = await import("firebase/firestore");
        const whitelistQuery = query(
          collection(db, "whitelist"),
          where("email", "==", userEmail.toLowerCase()),
        );
        const snapshot = await getDocs(whitelistQuery);
        if (!snapshot.empty) {
          await updateDoc(snapshot.docs[0].ref, gradData);
        }
      }

      console.log("[PromoCode] Graduation info saved:", gradData);
      setStep("code"); // Advance to code input
    } catch (error) {
      console.error("[PromoCode] Error saving graduation info:", error);
      setGradError("Failed to save graduation info. Please try again.");
    } finally {
      setGradSaving(false);
    }
  };

  // --- Step B: Code Input handlers ---

  const handleCodeChange = (value) => {
    const upperValue = value.toUpperCase();
    setCode(upperValue);
    if (errors.code || errors.general) {
      setErrors({});
    }
  };

  const handleCodeComplete = (value) => {
    const upperValue = value.toUpperCase();
    setCode(upperValue);
    console.log("[PromoCode] Code complete:", upperValue, "Length:", upperValue.length);
  };

  const validateForm = (codeToValidate) => {
    const testCode = codeToValidate || code;
    console.log("[PromoCode] Validating code:", testCode, "Length:", testCode.length);

    const newErrors = {};

    if (testCode.length !== 6) {
      newErrors.code = "Please enter the complete 6-character code";
    } else if (!/^[A-Z0-9]{6}$/i.test(testCode)) {
      newErrors.code = "Code must contain only letters and numbers";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit the access code — calls the unified redeemAccessCode cloud function
  const handleSubmit = async (e, codeOverride = null) => {
    if (e) e.preventDefault();

    const codeToSubmit = codeOverride || code;

    if (!validateForm(codeToSubmit)) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      if (!userEmail || !userId) {
        setErrors({ general: "User information not available. Please try logging in again." });
        setIsLoading(false);
        return;
      }

      // Call the unified access code function (handles both promo and org codes)
      const functions = getFunctions();
      const redeemAccessCode = httpsCallable(functions, "redeemAccessCode");

      const result = await redeemAccessCode({
        code: codeToSubmit.toUpperCase(),
        userId: userId,
        userEmail: userEmail,
      });

      if (result.data.success) {
        setSuccess(true);
        console.log("Access code redeemed successfully:", result.data);

        // Reload page after 2 seconds to refresh user data
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      console.error("Error redeeming access code:", error);

      // Handle specific error messages from Firebase
      if (error.code === "functions/not-found") {
        setErrors({ general: "Invalid access code. Please check and try again." });
      } else if (error.code === "functions/failed-precondition") {
        setErrors({ general: "This access code has reached its redemption limit." });
      } else if (error.code === "functions/already-exists") {
        setErrors({ general: "You have already redeemed this access code." });
      } else if (error.message) {
        setErrors({ general: error.message });
      } else {
        setErrors({ general: "An unexpected error occurred. Please try again." });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setCode("");
    setErrors({});
    setSuccess(false);
  };

  // --- Render: Success screen ---
  if (success) {
    return (
      <div className="w-full max-w-md mx-auto flex flex-col items-center gap-4">
        <div className="flex justify-center mb-2">
          <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
            <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <p className="text-xl font-semibold text-center">Code Applied Successfully!</p>
        <p className="text-sm text-muted-foreground text-center">
          Your account has been upgraded with the associated benefits.
        </p>
        <Button variant="outline" onClick={handleClear}>
          Apply Another Code
        </Button>
      </div>
    );
  }

  // --- Render: Step A — Graduation Gate ---
  if (step === "graduation") {
    return (
      <div className="flex flex-col items-center gap-4" style={{ maxWidth: "320px", margin: "0 auto" }}>
        <div className="flex flex-col items-center gap-1">
          <Label className="text-center text-muted-foreground text-sm">Graduation Date</Label>
          <p className="text-xs text-muted-foreground text-center">
            Enter your graduation date to input your code.
          </p>
        </div>

        {gradError && (
          <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 dark:text-red-400 dark:bg-red-950/50 dark:border-red-800 rounded-md text-center w-full">
            {gradError}
          </div>
        )}

        {/* Month selector */}
        <div className="w-full">
          <label
            htmlFor="grad-month"
            className="block text-sm font-medium text-muted-foreground mb-1"
          >
            Month
          </label>
          <select
            id="grad-month"
            value={selectedMonth}
            onChange={(e) => { setSelectedMonth(e.target.value); setGradError(""); }}
            disabled={gradSaving}
            className="w-full rounded-md border border-input bg-background text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Select month</option>
            {monthNames.map((name, i) => (
              <option key={i + 1} value={i + 1}>{name}</option>
            ))}
          </select>
        </div>

        {/* Year selector */}
        <div className="w-full">
          <label
            htmlFor="grad-year"
            className="block text-sm font-medium text-muted-foreground mb-1"
          >
            Year
          </label>
          <select
            id="grad-year"
            value={selectedYear}
            onChange={(e) => { setSelectedYear(e.target.value); setGradError(""); }}
            disabled={gradSaving}
            className="w-full rounded-md border border-input bg-background text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Select year</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <Button
          onClick={handleGraduationSubmit}
          disabled={gradSaving || !selectedMonth || !selectedYear}
          style={{ width: "100%" }}
        >
          {gradSaving ? (
            <>
              <RefreshIcon className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            "Continue"
          )}
        </Button>
      </div>
    );
  }

  // --- Render: Step B — Access Code Input ---
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex flex-col items-center gap-1">
        <Label className="text-center text-muted-foreground text-sm">Access Code</Label>
        <p className="text-xs text-muted-foreground text-center">
          Enter the 6-character code from your organization
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col items-center gap-4">
        {errors.general && (
          <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 dark:text-red-400 dark:bg-red-950/50 dark:border-red-800 rounded-md text-center">
            {errors.general}
          </div>
        )}

        <div className="flex flex-col items-center gap-3">
          <InputOTP
            maxLength={6}
            value={code}
            onChange={handleCodeChange}
            onComplete={handleCodeComplete}
            disabled={isLoading}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
          {errors.code && (
            <p className="text-sm text-red-600 text-center">{errors.code}</p>
          )}
        </div>

        <div className="flex flex-col items-center gap-2" style={{ width: "100%" }}>
          <Button
            type="submit"
            disabled={isLoading || code.length < 6}
            style={{ width: "100%" }}
          >
            {isLoading ? (
              <>
                <RefreshIcon className="h-4 w-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              "Apply Code"
            )}
          </Button>

          {code.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              className="text-muted-foreground"
              style={{ width: "100%" }}
              onClick={handleClear}
            >
              Clear
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default PromoCodeBlock;
